import type { OrganizationRecord } from '@/models/system'

export interface SessionData {
  tokenName: string
  tokenValue: string
  username?: string
  needModify?: boolean
  version?: string
  userId?: string | number
  route?: unknown[]
  roles?: unknown[]
  permissions?: unknown[]
  orgName?: string
  orgId?: string | number
  picturePath?: string
  organization?: OrganizationRecord
}

const SESSION_KEY = 'ivus:session'
const ORGANIZATION_KEY = 'ivus:organization'

function isOrganizationRecord(value: unknown): value is OrganizationRecord {
  return Boolean(value && typeof value === 'object' && !Array.isArray(value))
}

export function loadOrganization(): OrganizationRecord | null {
  try {
    const value = localStorage.getItem(ORGANIZATION_KEY)
    if (!value) return null
    const organization: unknown = JSON.parse(value)
    return isOrganizationRecord(organization) ? organization : null
  } catch {
    return null
  }
}

export function saveOrganization(organization?: OrganizationRecord) {
  try {
    if (!organization) {
      localStorage.removeItem(ORGANIZATION_KEY)
      return
    }
    localStorage.setItem(ORGANIZATION_KEY, JSON.stringify(organization))
  } catch {
    // Organization persistence must not prevent a successful login in restricted storage contexts.
  }
}

export function clearOrganization() {
  localStorage.removeItem(ORGANIZATION_KEY)
}

export function loadSession(): SessionData | null {
  try {
    const value = localStorage.getItem(SESSION_KEY)
    if (!value) return null
    const stored = JSON.parse(value) as Partial<SessionData> & { token?: unknown; userName?: unknown }
    const tokenValue = typeof stored.tokenValue === 'string' ? stored.tokenValue : stored.token
    if (typeof tokenValue !== 'string' || !tokenValue.trim()) return null
    return {
      tokenName: typeof stored.tokenName === 'string' && stored.tokenName.trim() ? stored.tokenName : 'satoken',
      tokenValue,
      username:
        typeof stored.username === 'string'
          ? stored.username
          : typeof stored.userName === 'string'
            ? stored.userName
            : undefined,
      needModify: stored.needModify,
      version: typeof stored.version === 'string' ? stored.version : undefined,
      userId: stored.userId,
      route: stored.route,
      roles: stored.roles,
      permissions: stored.permissions,
      orgName: stored.orgName,
      orgId: stored.orgId,
      picturePath: stored.picturePath,
      organization: isOrganizationRecord(stored.organization) ? stored.organization : loadOrganization() || undefined
    }
  } catch {
    return null
  }
}

export function saveSession(session: SessionData) {
  try {
    localStorage.setItem(SESSION_KEY, JSON.stringify(session))
    if ('organization' in session) saveOrganization(session.organization)
  } catch {
    // Session persistence must not prevent a successful login in restricted storage contexts.
  }
}

export function clearSession() {
  localStorage.removeItem(SESSION_KEY)
  clearOrganization()
}
